import React, { useState, useEffect, useCallback, useRef } from 'react';
import ConfirmModal from '../../Common/ConfirmModal';
import AutoBlockConfigCard from './AutoBlockConfigCard';
import AutoBlockActiveBans from './AutoBlockActiveBans';
import AutoBlockHistoryTable from './AutoBlockHistoryTable';
import AutoBlockStats from './AutoBlockStats';
import useAutoBlockRealtime from '../../../hooks/useAutoBlockRealtime';
import './AutoBlock.css';
import {
  getAutoBlockConfig,
  updateAutoBlockConfig,
  triggerAutoBlockKillSwitch,
  getAutoBlockActiveBans,
  getAutoBlockStats,
  unbanAutoBlockEvent,
  unbanAndWhitelistEvent,
  getAutoBlockHealth,
  triggerAutoBlockReconcile,
} from '../../../services/fail2banApi';

// Polling rápido (fallback): quando Socket.IO desconectado, refresca a cada 30s.
// Polling lento (safety net): quando conectado, ainda refresca a cada 120s para
// pegar mudanças que escaparam (drift do reconciler etc.).
const POLL_FAST_MS = 30000;
const POLL_SLOW_MS = 120000;

function AutoBlockTab({ userRole, showAlert }) {
  // HARDGUARD: defesa em profundidade. Não confia só na visibilidade da aba.
  if (userRole !== 'root') {
    return null;
  }

  const [config, setConfig] = useState(null);
  const [activeBans, setActiveBans] = useState([]);
  const [stats, setStats] = useState(null);
  const [health, setHealth] = useState(null);
  const [busy, setBusy] = useState(false);
  const [historyKey, setHistoryKey] = useState(0);
  const [confirmModal, setConfirmModal] = useState(null);
  const pollRef = useRef(null);

  const loadAll = useCallback(async () => {
    try {
      const [cfgRes, bansRes, statsRes, healthRes] = await Promise.allSettled([
        getAutoBlockConfig(),
        getAutoBlockActiveBans(),
        getAutoBlockStats(7),
        getAutoBlockHealth(),
      ]);
      if (cfgRes.status === 'fulfilled') setConfig(cfgRes.value);
      if (bansRes.status === 'fulfilled') setActiveBans(bansRes.value.active || []);
      if (statsRes.status === 'fulfilled') setStats(statsRes.value);
      if (healthRes.status === 'fulfilled') setHealth(healthRes.value);
    } catch (err) {
      showAlert(err.response?.data?.error || 'Erro ao carregar dados', 'error');
    }
  }, [showAlert]);

  // Push real-time via Socket.IO + fallback polling adaptativo.
  const { connected: rtConnected } = useAutoBlockRealtime((event) => {
    // Recarrega listas; também emite toast curto pra dar feedback visual.
    loadAll();
    setHistoryKey((k) => k + 1);
    if (event.type === 'security.auto_block.banned') {
      const p = event.payload || {};
      showAlert(`Novo ${p.dryRun ? 'evento dry-run' : 'ban'}: ${p.ip}`, 'success');
    } else if (event.type === 'security.auto_block.unbanned') {
      const p = event.payload || {};
      showAlert(`Desbanido: ${p.ip} (${p.unbanReason || 'expirado'})`, 'success');
    } else if (event.type === 'security.auto_block.kill_switch_triggered') {
      const p = event.payload || {};
      showAlert(`Kill-switch acionado: ${p.reason || ''}`, 'error');
    }
  });

  useEffect(() => {
    loadAll();
    const interval = rtConnected ? POLL_SLOW_MS : POLL_FAST_MS;
    pollRef.current = setInterval(loadAll, interval);
    return () => clearInterval(pollRef.current);
  }, [loadAll, rtConnected]);

  const handleSave = async (payload, version) => {
    setBusy(true);
    try {
      const updated = await updateAutoBlockConfig(payload, version);
      setConfig(updated);
      setHistoryKey((k) => k + 1);
      showAlert('Configuração salva com sucesso', 'success');
      await loadAll();
    } catch (err) {
      const msg = err.response?.data?.error || 'Erro ao salvar';
      const code = err.response?.data?.code;
      if (code === 'VERSION_MISMATCH') {
        showAlert('Configuração foi atualizada por outro admin. Recarregando…', 'error');
        await loadAll();
      } else if (code === 'PRECONDITION_FAILED') {
        const details = err.response?.data?.details || [];
        const items = details.map((d) => `${d.name}: ${d.info || 'falhou'}`).join('; ');
        showAlert(`Pre-flight falhou: ${items}`, 'error');
      } else {
        showAlert(msg, 'error');
      }
    } finally {
      setBusy(false);
    }
  };

  const handleKillSwitch = () => {
    setConfirmModal({
      title: 'Acionar kill-switch?',
      body: (
        <div>
          <p>Esta ação <strong>desativa o auto-block imediatamente</strong>:</p>
          <ul style={{ marginLeft: '1.5rem', listStyle: 'disc' }}>
            <li>Novos bans deixam de ser aplicados</li>
            <li>IPs já banidos <strong>permanecem</strong> até expirar naturalmente</li>
            <li>Para reativar, basta marcar "Auto-block ativo" e salvar</li>
          </ul>
        </div>
      ),
      confirmLabel: 'Sim, desativar agora',
      danger: true,
      onConfirm: async () => {
        setConfirmModal(null);
        setBusy(true);
        try {
          await triggerAutoBlockKillSwitch('manual_via_ui');
          showAlert('Kill-switch acionado', 'success');
          await loadAll();
        } catch (err) {
          showAlert(err.response?.data?.error || 'Erro ao acionar kill-switch', 'error');
        } finally {
          setBusy(false);
        }
      },
    });
  };

  const handleUnban = (ban) => {
    setConfirmModal({
      title: `Desbloquear ${ban.ip}?`,
      body: (
        <div>
          <p>Esta ação:</p>
          <ul style={{ marginLeft: '1.5rem', listStyle: 'disc' }}>
            <li>Remove o ban ativo imediatamente</li>
            <li><strong>Zera a escala de reincidência</strong></li>
            <li>Próximo ban deste IP volta para 15 min</li>
          </ul>
          <p style={{ marginTop: '0.75rem' }}>
            Use apenas se identificou que era falso positivo (NAT instável, cliente legítimo).
          </p>
        </div>
      ),
      confirmLabel: 'Sim, desbloquear',
      onConfirm: async () => {
        setConfirmModal(null);
        setBusy(true);
        try {
          const result = await unbanAutoBlockEvent(ban.id, true);
          if (result.stale === true) {
            // Fix #3 (wave 7): novo ban reincidiu entre carregamento da lista e click.
            showAlert(
              `IP ${ban.ip} desbloqueado, mas a escalação NÃO foi zerada — novo ban detectado para este IP. Atualize a lista.`,
              'warning'
            );
          } else if (result.escalation_reset === false) {
            showAlert(
              `IP ${ban.ip} desbloqueado, mas escalação não foi zerada. Tente novamente em alguns segundos.`,
              'error'
            );
          } else {
            showAlert(`IP ${ban.ip} desbloqueado e escalação zerada`, 'success');
          }
          setActiveBans((bans) => bans.filter((b) => b.id !== ban.id));
          setHistoryKey((k) => k + 1);
          await loadAll();
        } catch (err) {
          showAlert(err.response?.data?.error || 'Erro ao desbloquear', 'error');
        } finally {
          setBusy(false);
        }
      },
    });
  };

  const handleWhitelist = (ban) => {
    setConfirmModal({
      title: `Adicionar ${ban.ip} à whitelist permanente?`,
      body: (
        <div>
          <p>Esta ação:</p>
          <ul style={{ marginLeft: '1.5rem', listStyle: 'disc' }}>
            <li>Desbloqueia o IP imediatamente</li>
            <li>Adiciona à whitelist global do fail2ban</li>
            <li><strong>Não banirá novamente</strong>, mesmo em violações futuras</li>
            <li>Reverter exige remoção manual da whitelist</li>
          </ul>
        </div>
      ),
      confirmLabel: 'Sim, adicionar à whitelist',
      danger: true,
      onConfirm: async () => {
        setConfirmModal(null);
        setBusy(true);
        try {
          await unbanAndWhitelistEvent(ban.id);
          showAlert(`IP ${ban.ip} desbloqueado e adicionado à whitelist permanente`, 'success');
          setActiveBans((bans) => bans.filter((b) => b.id !== ban.id));
          setHistoryKey((k) => k + 1);
          await loadAll();
        } catch (err) {
          showAlert(err.response?.data?.error || 'Erro ao adicionar à whitelist', 'error');
        } finally {
          setBusy(false);
        }
      },
    });
  };

  const handleReconcile = async () => {
    setBusy(true);
    try {
      const r = await triggerAutoBlockReconcile();
      if (r.success) {
        showAlert(
          `Reconciliação: drift_forward=${r.drift_forward}, drift_backward=${r.drift_backward}, expirados=${r.expired_marked}`,
          'success'
        );
      } else {
        showAlert(`Reconciliação falhou: ${r.error || 'erro desconhecido'}`, 'error');
      }
      await loadAll();
      setHistoryKey((k) => k + 1);
    } catch (err) {
      showAlert(err.response?.data?.error || 'Erro ao reconciliar', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ab-tab">
      <div className="ab-section">
        <div className="ab-section-header">
          <h3>Configuração</h3>
          <button
            type="button"
            className="ab-btn-secondary"
            onClick={handleReconcile}
            disabled={busy}
            title="Sincroniza estado do fail2ban com o histórico no banco"
          >
            🔁 Forçar Atualizar
          </button>
        </div>
        <AutoBlockConfigCard
          config={config}
          killSwitch={health?.kill_switch}
          onSave={handleSave}
          onKillSwitch={handleKillSwitch}
          busy={busy}
        />
      </div>

      <div className="ab-section">
        <h3>Estatísticas</h3>
        <AutoBlockStats stats={stats} killSwitch={health?.kill_switch} />
      </div>

      <div className="ab-section">
        <div className="ab-section-header">
          <h3>Bans ativos ({activeBans.length})</h3>
          <button
            type="button"
            className="ab-btn-secondary"
            onClick={loadAll}
            disabled={busy}
          >
            ⟳ Atualizar
          </button>
        </div>
        <AutoBlockActiveBans
          activeBans={activeBans}
          busy={busy}
          onUnban={handleUnban}
          onWhitelist={handleWhitelist}
        />
      </div>

      <div className="ab-section">
        <h3>Histórico</h3>
        <AutoBlockHistoryTable refreshKey={historyKey} />
      </div>

      <ConfirmModal
        open={!!confirmModal}
        title={confirmModal?.title}
        body={confirmModal?.body}
        confirmLabel={confirmModal?.confirmLabel}
        danger={confirmModal?.danger}
        loading={busy}
        onCancel={() => setConfirmModal(null)}
        onConfirm={confirmModal?.onConfirm}
      />
    </div>
  );
}

export default AutoBlockTab;
